import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { ArrowRight, FileText } from 'lucide-react';
import { getPayment } from '../api/payments';

export function PaymentSuccessPage() {
  const [params] = useSearchParams();
  const [payment, setPayment] = useState(null);

  const paymentId = params.get('payment_id');

  useEffect(() => {
    // Iframe içindeysek parent'a sinyal yolla (mevcut davranışı koru)
    if (window.parent !== window) {
      window.parent.postMessage({
        type: 'payment-result',
        status: 'success',
        paymentId,
      }, '*');
    }

    if (paymentId) {
      getPayment(paymentId)
        .then(d => setPayment(d.payment))
        .catch(() => {});
    }
  }, [paymentId]);

  // Plan adına göre kullanıcı sayısı (görsel için, payment yoksa fallback)
  const userLimits = { starter: '5', business: '15', pro: '50', enterprise: '∞' };
  const userLimit = payment ? (userLimits[payment.plan_name] || '∞') : '5';

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' })
      .format(Number(amount || 0));

  const planLabels = {
    starter: 'Starter',
    business: 'Business',
    pro: 'Pro',
    enterprise: 'Enterprise',
  };
  const planName = payment ? (planLabels[payment.plan_name] || payment.plan_name) : '';
  const cycleName = payment?.billing_cycle === 'yearly' ? 'Yıllık' : 'Aylık';

  return (
    <div
      className="min-h-screen relative overflow-hidden flex items-center justify-center px-6 py-12"
      style={{
        background: `
          radial-gradient(circle at 20% 80%, rgba(37,99,235,0.18) 0%, transparent 50%),
          radial-gradient(circle at 80% 20%, rgba(251,191,36,0.14) 0%, transparent 50%),
          radial-gradient(circle at 50% 50%, rgba(6,182,212,0.08) 0%, transparent 60%),
          #0A0F1E
        `,
      }}
    >
      {/* Konfeti animasyonu */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <span className="confetti confetti-1" />
        <span className="confetti confetti-2" />
        <span className="confetti confetti-3" />
        <span className="confetti confetti-4" />
        <span className="confetti confetti-5" />
        <span className="confetti confetti-6" />
        <span className="confetti confetti-7" />
        <span className="confetti confetti-8" />
        <span className="confetti confetti-9" />
        <span className="confetti confetti-10" />
      </div>

      <style>{`
        .confetti {
          position: absolute;
          opacity: 0.7;
          border-radius: 2px;
        }
        .confetti-1 { top: 10%; left: 8%; width: 8px; height: 8px; background: #FBBF24; transform: rotate(20deg); animation: float 6s ease-in-out infinite; }
        .confetti-2 { top: 18%; right: 12%; width: 6px; height: 18px; background: #06B6D4; transform: rotate(-15deg); animation: float 8s ease-in-out infinite reverse; }
        .confetti-3 { top: 70%; left: 14%; width: 12px; height: 8px; background: #F472B6; transform: rotate(45deg); animation: float 7s ease-in-out infinite; animation-delay: 1s; }
        .confetti-4 { top: 80%; right: 10%; width: 10px; height: 10px; background: #84CC16; border-radius: 50%; animation: float 9s ease-in-out infinite reverse; }
        .confetti-5 { top: 25%; right: 22%; width: 6px; height: 6px; background: #FBBF24; border-radius: 50%; animation: float 5s ease-in-out infinite; animation-delay: 2s; }
        .confetti-6 { top: 55%; left: 4%; width: 14px; height: 6px; background: #06B6D4; transform: rotate(60deg); animation: float 8s ease-in-out infinite; animation-delay: 0.5s; }
        .confetti-7 { top: 35%; left: 88%; width: 8px; height: 12px; background: #F472B6; transform: rotate(30deg); animation: float 7s ease-in-out infinite; animation-delay: 1.5s; }
        .confetti-8 { top: 62%; right: 30%; width: 10px; height: 10px; background: #FBBF24; transform: rotate(-30deg); animation: float 6s ease-in-out infinite; animation-delay: 0.8s; }
        .confetti-9 { top: 88%; left: 45%; width: 8px; height: 8px; background: #84CC16; transform: rotate(15deg); animation: float 8s ease-in-out infinite; animation-delay: 2.5s; }
        .confetti-10 { top: 15%; left: 50%; width: 6px; height: 14px; background: #06B6D4; transform: rotate(-45deg); animation: float 7s ease-in-out infinite; animation-delay: 0.3s; }

        @keyframes float {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          50% { transform: translateY(-24px) rotate(180deg); }
        }

        @keyframes pulse-dot {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(1.3); }
        }

        .gradient-title {
          background: linear-gradient(135deg, #2563EB 0%, #06B6D4 50%, #FBBF24 100%);
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
        }

        .pulse-dot {
          animation: pulse-dot 1.5s ease-in-out infinite;
        }
      `}</style>

      <div className="relative z-10 w-full max-w-xl">
        {/* Badge */}
        <div className="inline-flex items-center gap-2 mb-6"
          style={{
            background: 'rgba(37,99,235,0.15)',
            border: '1px solid rgba(37,99,235,0.3)',
            padding: '6px 14px',
            borderRadius: '100px',
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: '11px',
            letterSpacing: '0.15em',
            textTransform: 'uppercase',
            color: '#60A5FA',
          }}
        >
          <span
            className="pulse-dot"
            style={{
              width: '6px',
              height: '6px',
              background: '#60A5FA',
              borderRadius: '50%',
              boxShadow: '0 0 12px #60A5FA',
            }}
          />
          Aboneliğiniz aktif
        </div>

        {/* Title */}
        <h1
          className="gradient-title"
          style={{
            fontFamily: "'Bowlby One', cursive",
            fontSize: 'clamp(64px, 10vw, 96px)',
            lineHeight: 0.85,
            letterSpacing: '-0.04em',
            marginBottom: '8px',
            textTransform: 'uppercase',
          }}
        >
          TEBRİKLER!
        </h1>

        {/* Subtitle */}
        <h2
          style={{
            fontFamily: "'Outfit', sans-serif",
            fontWeight: 700,
            fontSize: 'clamp(24px, 3vw, 32px)',
            lineHeight: 1.1,
            marginBottom: '16px',
            color: 'white',
          }}
        >
          Sahaya hoş geldin.
        </h2>

        {/* Body */}
        <p
          style={{
            fontFamily: "'Outfit', sans-serif",
            fontSize: '16px',
            lineHeight: 1.55,
            color: '#94A3B8',
            marginBottom: '32px',
            maxWidth: '440px',
          }}
        >
          Ödemen başarıyla alındı. Faturan e-posta adresine gönderildi. Şimdi gerçek iş başlıyor —
          ekibini ekle, ilk işi oluştur, mobil uygulamayı indir.
        </p>

        {/* Stat kartları */}
        {payment && (
          <div className="grid grid-cols-3 gap-3 mb-8">
            <StatCard value={planName} label="Plan" />
            <StatCard value={cycleName} label="Periyot" />
            <StatCard value={formatCurrency(payment.total_amount)} label="Tutar" small />
          </div>
        )}

        {!payment && (
          <div className="grid grid-cols-3 gap-3 mb-8">
            <StatCard value={userLimit} label="Kullanıcı" />
            <StatCard value="∞" label="İş kaydı" />
            <StatCard value="30" label="Gün geçerli" />
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3 flex-wrap">
          <Link to="/" className="flex-1 min-w-[200px]">
            <button
              className="w-full"
              style={{
                padding: '16px 28px',
                borderRadius: '12px',
                border: 'none',
                fontFamily: "'Outfit', sans-serif",
                fontWeight: 700,
                fontSize: '15px',
                cursor: 'pointer',
                background: 'linear-gradient(135deg, #2563EB 0%, #06B6D4 100%)',
                color: 'white',
                boxShadow: '0 4px 24px rgba(37,99,235,0.4)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 8px 32px rgba(37,99,235,0.5)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 24px rgba(37,99,235,0.4)';
              }}
            >
              Hadi başlayalım
              <ArrowRight size={16} />
            </button>
          </Link>

          <Link to="/faturalar">
            <button
              style={{
                padding: '16px 28px',
                borderRadius: '12px',
                border: '1px solid rgba(255,255,255,0.12)',
                fontFamily: "'Outfit', sans-serif",
                fontWeight: 700,
                fontSize: '15px',
                cursor: 'pointer',
                background: 'rgba(255,255,255,0.06)',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'background 0.2s ease',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; }}
            >
              <FileText size={16} />
              Faturam
            </button>
          </Link>
        </div>

        {/* Footer */}
        <div
          style={{
            marginTop: '24px',
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: '11px',
            color: '#64748B',
            letterSpacing: '0.05em',
          }}
        >
          {paymentId && <>İşlem · {paymentId.slice(0, 8)} · </>}
          <a
            href="mailto:destek@fieldpin.io"
            style={{ color: '#60A5FA', textDecoration: 'none' }}
          >
            destek@fieldpin.io
          </a>
        </div>
      </div>
    </div>
  );
}

function StatCard({ value, label, small = false }) {
  return (
    <div
      style={{
        background: 'rgba(255,255,255,0.03)',
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: '12px',
        padding: '16px',
        textAlign: 'center',
      }}
    >
      <div
        style={{
          fontFamily: "'Bowlby One', cursive",
          fontSize: small ? '20px' : '28px',
          color: '#FBBF24',
          marginBottom: '4px',
          letterSpacing: '-0.02em',
          lineHeight: 1.1,
          wordBreak: 'break-word',
        }}
      >
        {value}
      </div>
      <div
        style={{
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: '10px',
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          color: '#94A3B8',
        }}
      >
        {label}
      </div>
    </div>
  );
}
